import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactLoading from 'react-loading';

import SupTripPackUpload from './supTripPackUpload/SupTripPackUpload';
import SupGuidePackUpload from './supGuidePackUpload/SupGuidePackUpload';
import SupActivityPackUpload from './supActivityPackUpload/SupActivityPackUpload';
import AccommodationForm from '../accommodation/AccommodationForm';
import { supOrgTripDetailsFun } from '../../api/supportAdmin';

/*
 * mode here is the pack-type letter (t/g/a/c), matched from the
 * route /support/create/pack/:orgId (see SupportLayout.jsx) - not
 * to be confused with the approval-status "mode" used elsewhere.
 *
 * For accommodation (mode === 'c') this wrapper used to always
 * render a blank create form, ignoring the ?new=false&type=edit&
 * id=... query params the pack list's "Edit" button already sends
 * (see SupOrgPackList.jsx's handleEdit). That meant clicking Edit
 * on an existing accommodation never actually loaded it - the form
 * opened in create mode and fell back to whatever unrelated draft
 * happened to be sitting in localStorage. Fixed by fetching the
 * record here (same admin endpoint - type='camp' - the backend
 * already exposes for support) and handing it to AccommodationForm
 * as initialData, exactly like OrgAccommodationUpload.jsx does for
 * the organizer side.
 */
const SupportPackUploadWrapper = () => {
    const { mode, orgId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const searchParams = new URLSearchParams(location.search);

    /*
     * "new=false" means this isn't a blank pack - it's either an
     * edit or a duplicate of an existing one, both of which need
     * the existing record's data loaded into the form. "type"
     * tells the two apart: edit submits as an UPDATE to the same
     * record, duplicate submits as a brand new CREATE seeded with
     * a copy of the data. Only fetch drives isEdit vs duplicate -
     * whether we load data at all is the same for both.
     */
    const isExistingPack = searchParams.get('new') === 'false';

    const isDuplicate = searchParams.get('type') === 'duplicate';

    const isEdit = isExistingPack && !isDuplicate;

    const accommodationId = searchParams.get('id');

    const [initialData, setInitialData] = useState(null);
    const [loading, setLoading] = useState(mode === 'c' && isExistingPack);

    useEffect(() => {
        if (mode !== 'c') {
            return;
        }

        if (!isExistingPack || !accommodationId) {
            setInitialData(null);
            setLoading(false);
            return;
        }

        let cancelled = false;

        async function loadAccommodation() {
            setLoading(true);

            try {
                const result = await supOrgTripDetailsFun(accommodationId, 'camp');

                if (!cancelled) {
                    setInitialData(result?.data || null);
                }
            } catch (error) {
                if (!cancelled) {
                    const message = error?.response?.data?.message || error?.message || 'Failed to load accommodation';

                    toast.error(message);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        loadAccommodation();

        return () => {
            cancelled = true;
        };
    }, [mode, isExistingPack, accommodationId]);

    if (mode === 'c') {
        if (loading) {
            return (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            );
        }

        if (isExistingPack && !initialData) {
            return (
                <div className='orgAccommodationUploadError'>
                    <h3>Couldn't load this accommodation</h3>
                    <button type='button' onClick={() => navigate(`/support/c/packages/${orgId}`)}>
                        Back to Packages
                    </button>
                </div>
            );
        }

        return (
            <AccommodationForm
                mode={isEdit ? 'edit' : 'create'}
                role='support'
                orgId={orgId}
                accommodationId={isEdit ? accommodationId : null}
                initialData={initialData}
                onSuccess={() => navigate(`/support/c/packages/${orgId}`)}
            />
        );
    }

    const SupPackUploadComponents = {
        t: SupTripPackUpload,
        g: SupGuidePackUpload,
        a: SupActivityPackUpload
    };

    const SupPackUploadComponent = SupPackUploadComponents[mode];

    return SupPackUploadComponent ? <SupPackUploadComponent mode='create' role='support' /> : null;
};

export default SupportPackUploadWrapper;
